import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { useEnforcement } from '@/hooks/useEnforcement';
import { useSubscription } from '@/hooks/useSubscription';
import { handleUpgrade, getCurrency } from '@/lib/checkout';
import {
  defaultBillingCycle,
  formatPlanPrice,
  isCapTier,
  lookupForCycle,
  planDisplayName,
} from '@/lib/plans';
import { numberLocale } from '@/lib/language';
import { trackConversion } from '@/lib/analytics';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle2, AlertTriangle, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { NextStepCta } from '@/components/journey/NextStepCta';
import { ADS_CHAPTER_TARGET } from '@/lib/journeyTargets';

interface UploadSuccessDialogProps {
  open: boolean;
  onClose: () => void;
  uploadedCount: number;
  failedCount: number;
}

// Konfetti-Toene aus {primary, success, warning} (D31/E18), kein violet mehr:
// `--violet` war seit dem Petrol-und-Sand-Umbau nur noch ein Alias auf
// primary und ist in S16 (PR 12) ganz entfallen; zwei der sechs Punkte
// waeren also nur ein Duplikat der primary-Punkte gewesen.
// Uebersehene Stelle des Konfetti-Widerspruchs
// (S3, siehe OptimizationProgressModal.tsx fuer dieselbe Regel).
const SPARKLE_DOTS: Array<{
  top: string;
  left?: string;
  right?: string;
  bg: string;
  delay: string;
}> = [
  { top: '15%', left: '12%', bg: 'hsl(var(--primary))', delay: '0s' },
  { top: '10%', right: '18%', bg: 'hsl(var(--warning))', delay: '0.15s' },
  { top: '25%', left: '30%', bg: 'hsl(var(--success))', delay: '0.25s' },
  { top: '20%', right: '10%', bg: 'hsl(var(--primary))', delay: '0.35s' },
  { top: '8%', left: '50%', bg: 'hsl(var(--warning))', delay: '0.1s' },
  { top: '30%', right: '35%', bg: 'hsl(var(--success))', delay: '0.4s' },
];

export const UploadSuccessDialog: React.FC<UploadSuccessDialogProps> = ({
  open,
  onClose,
  uploadedCount,
  failedCount,
}) => {
  const { t, i18n } = useTranslation('dashboard');
  const navigate = useNavigate();

  // Upsell-Zeile nach dem Muster des Erfolgs-Scoreboards (C-6): der
  // Upload-Erfolg ist derselbe emotionale Hochpunkt, und das Event
  // success_dialog_upsell_clicked (Bericht 8.3 Punkt 6) braucht einen fachlich
  // richtigen Klickpfad — der Dialog hatte bisher keinen Upsell-CTA. Gate wie
  // im Scoreboard: nur wenn das Konto mehr Produkte haelt, als der Tarif
  // abdeckt, und es einen Zieltarif gibt. Bezugsgroesse ist kontoweit.
  const { limit, totalProducts, plan, recommendedPlan, nextPlan } = useEnforcement();
  const { subscription } = useSubscription();
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const effectivePlan = recommendedPlan || nextPlan;
  // CRO 21.08.2026: Neuabschluesse checken mit Jahresabrechnung aus,
  // Bestandskunden behalten ihr Intervall. Nur fuer Baender (Legacy-Tarife
  // sind nicht mehr kaufbar; recommended/next liefern ohnehin nur Baender).
  const cycle = effectivePlan && isCapTier(effectivePlan.tier)
    ? defaultBillingCycle(subscription)
    : 'monthly';
  // `productsBeyondPlan > 0` war ein zweites Mal dasselbe Gate: bei
  // `totalProducts > limit` ist die Differenz per Definition positiv, und
  // `Math.max(0, positiv)` bleibt positiv. Beide Groessen kommen als `number`
  // aus `useEnforcement` (`count || 0` bzw. `product_limit ?? 200`), NaN ist
  // also ausgeschlossen — und selbst dann waere schon der erste Vergleich falsch.
  const productsBeyondPlan = Math.max(0, totalProducts - limit);
  const showUpsellRow = productsBeyondPlan > 0 && !!effectivePlan;

  const handleUpsellClick = async () => {
    if (!effectivePlan) return;
    trackConversion('success_dialog_upsell_clicked', { plan, target: effectivePlan.tier });
    setUpgradeLoading(true);
    try {
      const res = await handleUpgrade(lookupForCycle(effectivePlan, cycle), getCurrency());
      // Abgebrochene B2B-Bestaetigung (oder kein Redirect): Spinner loesen.
      if (!res.redirected) setUpgradeLoading(false);
    } catch (error) {
      Sentry.captureException(error, { tags: { feature: 'billing', action: 'upgrade-from-upload-success' } });
      setUpgradeLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md celebration-container overflow-hidden">
        {/* Sparkle burst overlay */}
        {open && (
          <div className="celebration-sparkle" aria-hidden="true">
            {SPARKLE_DOTS.map((dot, i) => (
              <div
                key={i}
                className="sparkle-dot"
                style={{
                  top: dot.top,
                  left: dot.left,
                  right: dot.right,
                  background: dot.bg,
                  animationDelay: dot.delay,
                }}
              />
            ))}
          </div>
        )}

        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className={open ? 'animate-success-bounce' : ''}>
              <CheckCircle2 className="h-6 w-6 text-success-ink" />
            </div>
            {t('uploadSuccess.title')}
          </DialogTitle>
          <DialogDescription>
            {t('uploadSuccess.productsPushed', { count: uploadedCount })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {failedCount > 0 && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4" />
              {t('uploadSuccess.productsFailed', { count: failedCount })}
            </div>
          )}

          <p className="text-sm text-muted-foreground">
            {t('uploadSuccess.propagationNote')}
          </p>

          {showUpsellRow && (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-primary/30 bg-accent/30 px-3.5 py-2.5">
              <p className="text-xs text-muted-foreground leading-snug">
                {/* Preisumbau: die Zeile nennt jetzt auch das Ziel-Band samt
                    Preis — der CTA daneben hat damit einen bezifferten
                    naechsten Schritt statt nur "mehr freischalten". Der
                    Jahres-Zweig sagt /Jahr (voller Jahresbetrag, CRO 21.08.). */}
                {t(cycle === 'yearly' ? 'uploadSuccess.upsellLineYearly' : 'uploadSuccess.upsellLine', {
                  remaining: productsBeyondPlan.toLocaleString(numberLocale(i18n.language)),
                  limit: limit.toLocaleString(numberLocale(i18n.language)),
                  plan: effectivePlan ? planDisplayName(effectivePlan, i18n?.language, t) : '',
                  price: effectivePlan ? formatPlanPrice(effectivePlan, getCurrency(), cycle) : '',
                })}
              </p>
              <button
                type="button"
                onClick={handleUpsellClick}
                disabled={upgradeLoading}
                className="shrink-0 px-3 py-1.5 rounded-md text-xs font-bold text-primary-ink border border-primary/40 hover:bg-accent transition-colors disabled:opacity-50"
              >
                {t('uploadSuccess.upsellCta')}
              </button>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">
            {t('uploadSuccess.close')}
          </Button>
          {/* Der Weg ins Merchant Center bleibt, verliert aber die gefuellte
              Flaeche: der naechste Schritt der Leiter steht daneben, und zwei
              gefuellte Knoepfe waeren zwei Primaeraktionen (Verbot 10 des
              UX-Audits). */}
          <Button
            variant="outline"
            className="w-full sm:w-auto gap-2"
            asChild
          >
            <a href="https://merchants.google.com" target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4" />
              {t('uploadSuccess.viewInMerchantCenter')}
            </a>
          </Button>
          {/* Uebergabe nach dem Upload (Spec 2026-09-16, Abschnitt 7): fuer den
              Kundenpfad gibt es danach genau EINE echte Handlung, das
              Google-Ads-Konto. Ist es schon verknuepft, beschriftet sich
              `NextStepCta` selbst als Weg zu den Zahlen -- welcher Schritt
              gemeint ist, entscheidet die Leiter, nicht dieser Dialog.
              `onAction` schliesst den Dialog VOR der Navigation: der Anker
              `#ads` klappt ein Kapitel der Startseite auf, und ein offener
              Dialog laege davor. */}
          <NextStepCta
            step="ads_connect"
            from="upload"
            className="w-full sm:w-auto"
            onAction={() => {
              onClose();
              navigate(ADS_CHAPTER_TARGET);
            }}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
